import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

/**
 * Manager Command Center
 *
 * The manager landing page. Summarizes operational blind spots (open NDRs,
 * channel sync failures, overdue / stale / on-hold orders, out-of / low-stock)
 * into prioritized cards. Data comes from GET /api/operations/summary.
 */

interface ConcernItem {
  id: number;
  primary: string;
  secondary?: string;
  meta?: string;
  order_id?: number;
  product_id?: number;
  created_at?: string | null;
  age_days?: number;
  is_stale?: boolean;
}

interface Concern {
  key: string;
  label: string;
  count: number | null;
  severity: 'critical' | 'warning' | 'ok' | 'unknown';
  action_view: string | null;
  items: ConcernItem[];
  error?: string;
  /** For open_ndrs: how many open NDRs are older than the list window. */
  stale_count?: number;
  window_days?: number;
}

interface OperationsSummary {
  generated_at: string;
  totals: {
    action_items: number;
    critical_areas: number;
    concern_areas: number;
  };
  concerns: Concern[];
}

interface ManagerCommandCenterProps {
  /** Navigate to another module (e.g. 'orders', 'logistics', 'inventory'). */
  onNavigate?: (view: string) => void;
  /**
   * Whether this view is the active/visible tab. When false, background
   * polling is paused so hidden tabs don't keep hitting the backend. Defaults
   * to true so the component works standalone.
   */
  isActive?: boolean;
}

const SEVERITY_STYLES: Record<string, { bg: string; border: string; text: string; dot: string; label: string }> = {
  critical: { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', dot: '#ef4444', label: 'Critical' },
  warning:  { bg: '#fffbeb', border: '#fcd34d', text: '#92400e', dot: '#f59e0b', label: 'Attention' },
  ok:       { bg: '#f0fdf4', border: '#86efac', text: '#166534', dot: '#10b981', label: 'Clear' },
  unknown:  { bg: '#f8f9fa', border: '#e2e8f0', text: '#64748b', dot: '#94a3b8', label: 'Unknown' },
};

const AlertIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const ManagerCommandCenter: React.FC<ManagerCommandCenterProps> = ({ onNavigate, isActive = true }) => {
  const [summary, setSummary] = useState<OperationsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    try {
      setError(null);
      const res = await api.get('/api/operations/summary');
      const data = res.data;
      // Validate the response shape before using it.
      if (!data || !Array.isArray(data.concerns)) {
        throw new Error('Unexpected response from operations summary');
      }
      setSummary(data as OperationsSummary);
    } catch (err: any) {
      console.error('Failed to load operations summary:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load command center');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only poll while this tab is active/visible. A hidden tab pauses polling
    // entirely and resumes (with an immediate refresh) when it becomes active.
    if (!isActive) return;
    fetchSummary();
    // Refresh every 60s so the manager view stays current without a reload.
    const interval = setInterval(fetchSummary, 60000);
    return () => clearInterval(interval);
  }, [fetchSummary, isActive]);

  if (loading && !summary) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
        Loading command center…
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px',
          padding: '16px 20px', color: '#991b1b', fontSize: '13px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
        }}>
          <span>Could not load the command center: {error}</span>
          <button className="btn btn-secondary btn-sm" onClick={() => { setLoading(true); fetchSummary(); }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const concerns = summary?.concerns ?? [];
  const totals = summary?.totals;

  return (
    <div style={{ padding: '20px', maxWidth: '1400px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>Command Center</h1>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
            What needs your attention right now
            {summary?.generated_at && (
              <span> · updated {new Date(summary.generated_at).toLocaleTimeString()}</span>
            )}
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => { setLoading(true); fetchSummary(); }}>
          Refresh
        </button>
      </div>

      {/* Headline totals */}
      {totals && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '26px', fontWeight: 700, color: '#0d9488' }}>{totals.action_items}</div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Open Action Items</div>
          </div>
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '26px', fontWeight: 700, color: totals.critical_areas > 0 ? '#ef4444' : '#10b981' }}>{totals.critical_areas}</div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Critical Areas</div>
          </div>
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '26px', fontWeight: 700, color: '#1e293b' }}>{totals.concern_areas}</div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Areas Monitored</div>
          </div>
        </div>
      )}

      {/* All clear state */}
      {totals && totals.action_items === 0 && (
        <div style={{
          background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '12px',
          padding: '24px', textAlign: 'center', color: '#166534', fontSize: '14px', fontWeight: 600,
        }}>
          All clear — no operational issues need your attention right now.
        </div>
      )}

      {/* Concern cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '16px' }}>
        {concerns.map((concern) => {
          const s = SEVERITY_STYLES[concern.severity] || SEVERITY_STYLES.unknown;
          const isEmpty = concern.count === 0;
          return (
            <div
              key={concern.key}
              style={{
                background: 'white',
                border: `1px solid ${isEmpty ? '#e2e8f0' : s.border}`,
                borderRadius: '12px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                opacity: isEmpty ? 0.7 : 1,
              }}
            >
              {/* Card header */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 16px', background: isEmpty ? '#f8fafb' : s.bg,
                borderBottom: `1px solid ${isEmpty ? '#f1f5f9' : s.border}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {!isEmpty && concern.severity !== 'ok' && <AlertIcon color={s.dot} />}
                  <span style={{ fontSize: '14px', fontWeight: 700, color: isEmpty ? '#64748b' : s.text }}>
                    {concern.label}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    display: 'inline-block', minWidth: '26px', textAlign: 'center',
                    padding: '2px 8px', borderRadius: '12px', fontSize: '13px', fontWeight: 700,
                    background: isEmpty ? '#e2e8f0' : s.dot, color: isEmpty ? '#64748b' : 'white',
                  }}>
                    {concern.count ?? '—'}
                  </span>
                </div>
              </div>

              {/* Card body */}
              <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                {concern.error ? (
                  <div style={{ padding: '16px', fontSize: '12px', color: '#991b1b' }}>{concern.error}</div>
                ) : isEmpty ? (
                  <div style={{ padding: '20px 16px', fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>
                    Nothing here — all clear.
                  </div>
                ) : (
                  concern.items.map((item) => (
                    <div
                      key={`${concern.key}-${item.id}`}
                      style={{
                        padding: '10px 16px', borderBottom: '1px solid #f1f5f9',
                        display: 'flex', flexDirection: 'column', gap: '2px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {item.primary}
                          {item.is_stale && (
                            <span style={{
                              fontFamily: "'Inter', sans-serif", fontSize: '10px', fontWeight: 600,
                              color: '#991b1b', background: '#fee2e2', border: '1px solid #fca5a5',
                              borderRadius: '6px', padding: '1px 6px', whiteSpace: 'nowrap',
                            }}>Stale</span>
                          )}
                        </span>
                        {item.meta && (
                          <span style={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {item.meta}
                          </span>
                        )}
                      </div>
                      {item.secondary && (
                        <span style={{ fontSize: '12px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.secondary}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Card footer — drill in */}
              {!isEmpty && concern.action_view && onNavigate && (
                <div style={{ padding: '10px 16px', borderTop: '1px solid #f1f5f9', textAlign: 'right' }}>
                  <button
                    onClick={() => onNavigate(concern.action_view!)}
                    style={{
                      background: 'none', border: 'none', color: '#0d9488',
                      fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: 0,
                    }}
                  >
                    {typeof concern.count === 'number' && concern.count > concern.items.length
                      ? `View all ${concern.count} →`
                      : 'Go to module →'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ManagerCommandCenter;
